import { faUser, faUserGroup, faBriefcaseBlank, faArrowTurnDownRight, faComment, faXmark, faUpFromLine, faBagShopping, faFileArrowUp } from '@fortawesome/pro-light-svg-icons'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { CustomerProjectModelTypeExtended, CustomerProjectActivityDataTy, CustomerProjectActivityTy } from '../types'
import { TwinCommentBox } from '../../../../baseComponents/TwinComment'
import { useCallback } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import ActivityDisplay, { ActivityDisplayComponent } from '../../../../baseComponents/ActivityDisplay'
import { QuoteOrderLogTypeKeys } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderLog'
import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { SellInvoiceLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellInvoice/sellInvoiceLog'
import { SellOrderLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderLog'
import { SellDeliveryNoteLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellDeliveryNote/sellDeliveryNoteLog'

interface CustomerProjectActivityTabProps {
    customerProjectData: CustomerProjectModelTypeExtended
    getCustomerProject: () => Promise<void>
}

const CustomerProjectActivityTab: React.FC<CustomerProjectActivityTabProps> = ({ customerProjectData, getCustomerProject }) => {
    const {t} = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    const customerProjectActivityData: CustomerProjectActivityDataTy[] = []
    const projectTypeDict: Record<ProjectTypesKeys, string> = {
        0: t('theOpportunity', 'la oportunidad'),
        1: t('theProject', 'el proyecto')
    }

    const customerProjectComments = customerProjectData?.CustomerProjectComments || []
    for (const element of customerProjectComments) {
        customerProjectActivityData.push({
            type: 'comment',
            data: element,
            date: element.createdAt
        })
    }

    const customerProjectStatusHistories = customerProjectData?.CustomerProjectStatusHistories || []
    for (const element of customerProjectStatusHistories) {
        customerProjectActivityData.push({
            type: 'status',
            data: element,
            date: element.createdAt
        })
    }
    const customerProjectEmployees = customerProjectData?.CustomerProjectEmployeeLogs || []
    for (const element of customerProjectEmployees) {
        customerProjectActivityData.push({
            type: element.deleted ? 'deletedEmployee' : 'assignedEmployee',
            data: {...element, projectTypeDict},
            date: element.createdAt || ''
        })
    }

    const customerProjectGroups = customerProjectData?.CustomerProjectGroupLogs || []
    for (const element of customerProjectGroups) {
        customerProjectActivityData.push({
            type: element.deleted ? 'deletedGroup' : 'assignedGroup',
            data: { ...element, projectTypeDict },
            date: element.createdAt || ''
        })
    }
    const customerProjectChangedTypes = customerProjectData?.CustomerProjectChangedTypeLogs || []
    for (const element of customerProjectChangedTypes) {
        if (!element.CustomerProjectTypeId) {
            customerProjectActivityData.push({
                type: 'turnedProject',
                data: element,
                date: element.createdAt || ''
            })
        } else {
            customerProjectActivityData.push({
                type: 'changedType',
                data: element,
                date: element.createdAt || ''
            })
        }
    }

    const customerProjectActives = customerProjectData?.CustomerProjectActiveLogs || []
    for (const element of customerProjectActives) {
        customerProjectActivityData.push({
            type: element.active ? 'reactivatedProject' : 'disabledProject',
            data: { ...element, projectTypeDict },
            date: element.createdAt || ''
        })
    }
    const quoteOrderLog: Record<QuoteOrderLogTypeKeys, CustomerProjectActivityTy>={
            0: 'creatingQuoteOrder',
            1: 'newVersionQuoteOrder',
            2: 'waitingResponseQuoteOrder',
            3: 'acceptedQuoteOrder',
            4: 'deniedQuoteOrder',
            5: 'newVersionCustomerQuoteOrder',
            6: 'acceptedCustomerQuoteOrder',
            7: 'deniedCustomerQuoteOrder',
            8: 'sent',
            9: 'processed',
        }
    const quoteOrdersLogs = customerProjectData?.CustomerProjectQuoteOrderLogs || []

    for (const log of quoteOrdersLogs) {
        customerProjectActivityData.push({
            type: quoteOrderLog[log.logType] as CustomerProjectActivityTy,
            data: { ...log },
            date: log.createdAt || ''
        })
    }

    const sellOrderLogType: Record<SellOrderLogTypeKeys, CustomerProjectActivityTy> = {
        0: 'creatingSellOrder',
        1: 'waitingResponseSellOrder',
        2: 'processedSellOrder'
    }

    const sellOrdersLogs = customerProjectData?.CustomerProjectSellOrderLogs || []
    for (const sellOrderLog of sellOrdersLogs) {
        customerProjectActivityData.push({
            type: sellOrderLogType[sellOrderLog.logType as SellOrderLogTypeKeys],
            data: sellOrderLog,
            date: sellOrderLog.createdAt || ''
        })
    }

    const sellDeliveryNoteLogType: Record<SellDeliveryNoteLogTypeKeys, CustomerProjectActivityTy> = {
        0: 'creatingSellDeliveryNote',
        1: 'waitingResponseSellDeliveryNote',
        2: 'processedSellDeliveryNote',
        3: 'refundedSellDeliveryNote'
    }

    const sellDeliveryNoteLogs = customerProjectData?.CustomerProjectSellDeliveryNoteLogs || []
    for (const sellDeliveryNoteLog of sellDeliveryNoteLogs) {
        customerProjectActivityData.push({
            type: sellDeliveryNoteLogType[sellDeliveryNoteLog.logType as SellDeliveryNoteLogTypeKeys],
            data: sellDeliveryNoteLog,
            date: sellDeliveryNoteLog.createdAt || ''
        })
    }

    const sellInvoiceLogType: Record<SellInvoiceLogTypeKeys, CustomerProjectActivityTy> = {
        0: 'creatingSellInvoice',
        1: 'waitingResponseSellInvoice',
        2: 'acceptedSellInvoice',
        3: 'deniedSellInvoice',
        4: 'refundedSellInvoice'
    }

    const sellInvoiceLogs = customerProjectData?.CustomerProjectSellInvoiceLogs || []
    for (const sellInvoiceLog of sellInvoiceLogs) {
        customerProjectActivityData.push({
            type: sellInvoiceLogType[sellInvoiceLog.logType as SellInvoiceLogTypeKeys],
            data: sellInvoiceLog,
            date: sellInvoiceLog.createdAt || ''
        })
    }

    customerProjectActivityData.sort((a, b) => {
        return new Date(b.date) > new Date(a.date) ? 1 : -1
    })

    customerProjectActivityData.push({
        type: 'createdAt',
        data: {
            projectType: customerProjectData.projectType
        },
        date: customerProjectData.createdAt
    })

    for (const key in customerProjectActivityData) {
        const element = customerProjectActivityData[key]
        if (element.type === 'turnedProject') {
            if (customerProjectActivityData[customerProjectActivityData.length - 1]) {
                customerProjectActivityData[customerProjectActivityData.length - 1].data.hasUpdatedToProject = true
            }
        }
        renderThis.push(<CustomerProjectActivityItem item={element} getData={getCustomerProject} key={key} />)
    }

    return (
        <ActivityDisplay renderThis={renderThis}/>
    )
}

interface CustomerProjectActivityItemProps {
    item: CustomerProjectActivityDataTy
    getData: () => Promise<void>
}

const CustomerProjectActivityItem: React.FC<CustomerProjectActivityItemProps> = ({ item, ...rest }) => {
    const componentsTypeDict: Record<CustomerProjectActivityTy, React.FC<CustomerProjectComponentProps>> = {
        'createdAt': CustomerProjectCreatedProjectComponent,
        'comment': CustomerProjectCommentComponent,
        'status': CustomerProjectHistoryComponent,
        'assignedEmployee': CustomerProjectEmployeeComponent,
        'assignedGroup': CustomerProjectGroupComponent,
        'deletedEmployee': CustomerProjectEmployeeDeletedComponent,
        'deletedGroup': CustomerProjectGroupDeletedComponent,
        'changedType': CustomerProjectChangedTypeComponent,
        'disabledProject': CustomerProjectDisabledComponent,
        'turnedProject': CustomerProjectOpTurnedProjectComponent,
        'reactivatedProject': CustomerProjectReactiveComponent,
        'creatingQuoteOrder': CustomerProjectCreatedQuoteOrderComponent,
        'newVersionQuoteOrder': CustomerProjectNewVersionQuoteOrderComponent,
        'waitingResponseQuoteOrder': CustomerProjectWaitingResponseQuoteOrderComponent,
        'acceptedQuoteOrder': CustomerProjectAcceptedQuoteOrderComponent,
        'deniedQuoteOrder': CustomerProjectDeniedQuoteOrderComponent,
        'newVersionCustomerQuoteOrder': CustomerProjectNewVersionCustomerQuoteOrderComponent,
        'acceptedCustomerQuoteOrder': CustomerProjectAcceptedCustomerQuoteOrderComponent,
        'deniedCustomerQuoteOrder': CustomerProjectDeniedCustomerQuoteOrderComponent,
        'sent': CustomerProjectSentCustomerQuoteOrderComponent,
        //aqui
        'processed': CustomerProjectSentCustomerQuoteOrderComponent,
        'creatingSellOrder': CustomerProjectCreatedSellOrderComponent,
        'waitingResponseSellOrder': CustomerProjectWaitingResponseSellOrderComponent,
        'processedSellOrder': CustomerProjectProcessedSellOrderComponent,
        'creatingSellDeliveryNote': CustomerProjectCreatedSellDeliveryNoteComponent,
        'waitingResponseSellDeliveryNote': CustomerProjectWaitingResponseSellDeliveryNoteComponent,
        'processedSellDeliveryNote': CustomerProjectProcessedSellDeliveryNoteComponent,
        'refundedSellDeliveryNote': CustomerProjectRefundedSellDeliveryNoteComponent,
        'creatingSellInvoice': CustomerProjectCreatedSellInvoiceComponent,
        'waitingResponseSellInvoice': CustomerProjectWaitingResponseSellInvoiceComponent,
        'acceptedSellInvoice': CustomerProjectAcceptedSellInvoiceComponent,
        'deniedSellInvoice': CustomerProjectDeniedSellInvoiceComponent,
        'refundedSellInvoice': CustomerProjectRefundedSellInvoiceComponent
    }
    const Component = componentsTypeDict[item.type]
    return (
        <Component item={item} {...rest} />
    )
}

interface CustomerProjectComponentProps {
    item: CustomerProjectActivityDataTy
    getData: () => Promise<void>
}

const CustomerProjectEmployeeComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={tVars('assignedToProjectOpp', 'Asignado a {{projectType}}', {projectType})} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Employee.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'assignedEmployeeActivityTab'} />
    )
}
const CustomerProjectGroupComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUserGroup} headerText={tVars('assignedToProjectOpp', 'Asignado a {{projectType}}', {projectType})} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Group.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'assignedGroupActivityTab'} />
    )
}
const CustomerProjectEmployeeDeletedComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    const projectType = item?.data?.projectType
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={t('removedFromProjectOpp', 'Eliminado del ') + (projectType === projectTypesInversed['project'] ? t('fromProject', 'del proyecto') : t('fromOpportunity', 'de la oportunidad'))} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Employee.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'removedEmployeeActivityTab'} />
    )
}
const CustomerProjectGroupDeletedComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    const projectType = item?.data?.projectType
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUserGroup} headerText={t('removedFromProjectOpp', 'Eliminado del ') + (projectType === projectTypesInversed['project'] ? t('fromProject', 'del proyecto') : t('fromOpportunity', 'de la oportunidad'))} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Group.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'removedGroupActivityTab'} />
    )
}

const CustomerProjectCreatedProjectComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent nameTrigger='' icon={faBriefcaseBlank} headerText={item.data.projectType === projectTypesInversed['project'] && !item?.data?.hasUpdatedToProject ? t('projectCreation', 'Creación del proyecto') : t('opportunityCreation', 'Creación de la oportunidad')} date={item.date} showImage={false} tooltipName='creationProject' />
    )
}

const CustomerProjectHistoryComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.EmployeeId} icon={faArrowTurnDownRight} mainColor={item?.data?.CustomerProjectStatus?.color} headerText={t('changedStatusToSm', 'Cambio de estado a')} image={item?.data?.Employee?.profile_image} date={item.data.createdAt} secondText={item?.data?.CustomerProjectStatus?.name} tooltipName={'statusChangedEmployeeActivityTab'} nameTrigger={item?.data?.Employee?.fullname_short} />
    )
}

const CustomerProjectCommentComponent: React.FC<CustomerProjectComponentProps> = ({ item, getData }) => {
    const { deleteCustomerProjectComment, updateCustomerProjectComment } = useCustomerProjectCommentComponentLogic({ getData })
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faComment} tooltipName={'commentEmployeeActivityTab'} headerText={t('newComment', 'Nuevo comentario')} image={item.data.Employee?.profile_image} date={item.date} nameTrigger={item.data.Employee.fullname_short} >
            <TwinCommentBox id={item.data.id} downloadFileURL='/api/app/customer/customerProject/customerProjectComment/getCustomerProjectCommentInstanceFile' comment={item.data.message || ''} deleteComment={deleteCustomerProjectComment} posArr={item.data.id} fieldComment={'message'} updateComment={updateCustomerProjectComment} EmployeeId={item.data?.Employee?.id} file_url={item.data?.file_url} />
        </ActivityDisplayComponent>
    )
}

interface CustomerProjectCommentComponentLogicProps {
    getData: () => Promise<void>
}

const useCustomerProjectCommentComponentLogic = ({getData}: CustomerProjectCommentComponentLogicProps) => {

const deleteCustomerProjectComment = useCallback(async (id: number) => {
    const result = await twinFetchPostJSON('/api/app/customer/customerProject/customerProjectComment/deleteCustomerProjectComment', { id })
    if (result) {
        await getData()
    }
}, [getData])
    const updateCustomerProjectComment = useCallback(async (id: number, message: string) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/customerProjectComment/updateCustomerProjectComment', { id, message })
        if(result){
            await getData()
        }
    }, [ getData ])
    return { deleteCustomerProjectComment, updateCustomerProjectComment }
}

const CustomerProjectChangedTypeComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faArrowTurnDownRight} mainColor={item?.data?.CustomerProjectStatus?.color} headerText={t('changedProjectType', 'Cambio de tipo a')} image={item?.data?.EmployeeTrigger?.profile_image} date={item.data.createdAt} secondText={item?.data?.CustomerProjectType?.name} tooltipName={'changedProjectTypeActivityTab'} nameTrigger={item?.data?.EmployeeTrigger?.fullname_short} />
    )
}
const CustomerProjectReactiveComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUpFromLine} mainColor={'#43BAA5'} headerText={tVars('reactivatedProjectOppS', 'Reactivado {{projectType}}', { projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'reactivatedProjectActivityTab'} />
    )
}
const CustomerProjectDisabledComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faXmark} mainColor={'#BA435D'} headerText={tVars('disabledProjectOppS', 'Desactivado {{projectType}}', { projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'disabledProjectActivityTab'} />
    )
}

const CustomerProjectOpTurnedProjectComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faFileArrowUp} mainColor={'#43BAA5'} headerText={t('convertedIntoProject', 'Convertido a proyecto')} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'turnedProjectActivityTab'} />
    )
}

const CustomerProjectCreatedQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdQuoteOrderN', 'Creación del presupuesto {{quoteOrderNum}}', { quoteOrderNum })}  image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdQuoteOrder' />
    )
}
const CustomerProjectNewVersionQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('newVersionQuoteOrderNum', 'Nueva versión de presupuesto {{quoteOrderNum}}', { quoteOrderNum })}  image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='newVersionQuoteOrder' />
    )
}
const CustomerProjectWaitingResponseQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseQuoteOrderNum', 'Esperando respuesta del presupuesto {{quoteOrderNum}}', { quoteOrderNum })}  image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseQuoteOrder' />
    )
}
const CustomerProjectAcceptedQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number 
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedQuoteOrderNum', 'Presupuesto {{quoteOrderNum}} acceptado', { quoteOrderNum })}  image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='acceptedQuoteOrder' />
    )
}
const CustomerProjectDeniedQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedQuoteOrderNum', 'Presupuesto {{quoteOrderNum}} denegado', { quoteOrderNum })}  image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='deniedQuoteOrder' />
    )
}

const CustomerProjectNewVersionCustomerQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    const version = item.data.Order?.version
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdVersionCustomerN', '{{customerName}} creó la versión {{version}} del presupuesto {{quoteOrderNum}}', { quoteOrderNum, version, customerName })} image={item.data.Order?.Customer?.image} date={item?.date} nameTrigger={customerName} tooltipName='newVersionCustomerQuoteOrder' showImage={false}/>
    )
}
const CustomerProjectAcceptedCustomerQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedCustomerN', '{{customerName}} marcó el presupuesto {{quoteOrderNum}} como aceptado', { quoteOrderNum, customerName })} image={item.data.Order?.Customer?.image} date={item.date} nameTrigger={customerName} tooltipName='acceptedCustomerQuoteOrder' showImage={false} />
    )
}
const CustomerProjectDeniedCustomerQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedCustomerN', '{{customerName}} marcó el presupuesto {{quoteOrderNum}} como denegado', { quoteOrderNum, customerName })} image={item.data.Order?.Customer?.image} date={item.date} nameTrigger={customerName} tooltipName='deniedCustomerQuoteOrder' showImage={false} />
    )
}
const CustomerProjectSentCustomerQuoteOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('sentCustomerN', 'Se envió el presupuesto {{quoteOrderNum}} a {{customerName}}', { quoteOrderNum, customerName })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={customerName} tooltipName='sentCustomerQuoteOrder' showImage={false} />
    )
}

const CustomerProjectCreatedSellOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellOrderN', 'Creación del pedido {{sellOrderNum}}', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellOrder' />
    )
}

const CustomerProjectProcessedSellOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('processedSellOrderNum', 'Pedido {{sellOrderNum}} procesado', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='processedSellOrder' />
    )
}

const CustomerProjectWaitingResponseSellOrderComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellOrderNum', 'Esperando respuesta del pedido {{sellOrderNum}}', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellOrder' />
    )
}

const CustomerProjectCreatedSellDeliveryNoteComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellDeliveryNoteN', 'Creación del albarán {{sellDeliveryNoteNum}}', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellDeliveryNote' />
    )
}

const CustomerProjectProcessedSellDeliveryNoteComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('processedSellDeliveryNoteNum', 'Albarán {{sellDeliveryNoteNum}} procesado', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='processedSellDeliveryNote' />
    )
}

const CustomerProjectWaitingResponseSellDeliveryNoteComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellDeliveryNoteNum', 'Esperando respuesta del albarán {{sellDeliveryNoteNum}}', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellDeliveryNote' />
    )
}

const CustomerProjectRefundedSellDeliveryNoteComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('refundedSellDeliveryNoteNum', 'Albarán {{sellDeliveryNoteNum}} devuelto', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='refundedSellDeliveryNote' />
    )
}

const CustomerProjectCreatedSellInvoiceComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellInvoiceN', 'Creación de la factura {{sellInvoiceNum}}', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellInvoice' />
    )
}

const CustomerProjectAcceptedSellInvoiceComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedSellInvoiceNum', 'factura {{sellInvoiceNum}} aceptada', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='acceptedSellInvoice' />
    )
}

const CustomerProjectWaitingResponseSellInvoiceComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellInvoiceNum', 'Esperando respuesta de la factura {{sellInvoiceNum}}', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellInvoice' />
    )
}

const CustomerProjectDeniedSellInvoiceComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedSellInvoiceNum', 'Factura {{sellInvoiceNum}} denegada', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='deniedSellInvoice' />
    )
}

const CustomerProjectRefundedSellInvoiceComponent: React.FC<CustomerProjectComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('refundedSellInvoiceNum', 'Factura {{sellInvoiceNum}} devuelta', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='refundedSellInvoice' />
    )
}

export default CustomerProjectActivityTab